'use strict'

const KMZ_TURN_SCOPE_PRIORITY = {
  playback: ['waylines', 'template'],
  templateExport: ['template', 'waylines'],
  waylinesExport: ['waylines', 'template']
}

const KMZ_TURN_LOCAL_CHILDREN = [
  'waypointTurnMode',
  'waypointTurnDampingDist'
]

const KMZ_TURN_ROOT_CACHE_PATHS = [
  'wpml:useGlobalTurnParam',
  'useGlobalTurnParam',
  'wpml:waypointTurnParam',
  'waypointTurnParam',
  'wpml:globalWaypointTurnMode',
  'globalWaypointTurnMode',
  'Folder.wpml:globalWaypointTurnMode',
  'Folder.globalWaypointTurnMode'
]

const KMZ_TURN_SCOPED_CACHE_PATHS = [
  'wpml:useGlobalTurnParam',
  'useGlobalTurnParam',
  'wpml:waypointTurnParam',
  'waypointTurnParam',
  'wpml:globalWaypointTurnMode',
  'globalWaypointTurnMode',
  'Folder.wpml:globalWaypointTurnMode',
  'Folder.globalWaypointTurnMode'
]

const fnCloneTurnValue = function (value) {
  if (Array.isArray(value)) {
    return value.map(fnCloneTurnValue)
  }
  if (value && typeof value === 'object') {
    const out = {}
    Object.keys(value).forEach((key) => {
      out[key] = fnCloneTurnValue(value[key])
    })
    return out
  }
  return value
}

const fnGetByPath = function (obj, path) {
  const parts = String(path || '').split('.').filter(Boolean)
  let current = obj
  for (let i = 0; i < parts.length; i += 1) {
    if (!current || typeof current !== 'object') return undefined
    current = current[parts[i]]
  }
  return current
}

const fnSetByPath = function (obj, path, value) {
  const parts = String(path || '').split('.').filter(Boolean)
  if (!parts.length || !obj || typeof obj !== 'object') return
  let current = obj
  for (let i = 0; i < parts.length - 1; i += 1) {
    const key = parts[i]
    if (!current[key] || typeof current[key] !== 'object' || Array.isArray(current[key])) {
      current[key] = {}
    }
    current = current[key]
  }
  current[parts[parts.length - 1]] = fnCloneTurnValue(value)
}

const fnDeletePath = function (obj, path) {
  const parts = String(path || '').split('.').filter(Boolean)
  if (!parts.length || !obj || typeof obj !== 'object') return
  const stack = [{ parent: null, key: '', value: obj }]
  let current = obj
  for (let i = 0; i < parts.length; i += 1) {
    if (!current || typeof current !== 'object') return
    const key = parts[i]
    if (!Object.prototype.hasOwnProperty.call(current, key)) return
    current = current[key]
    stack.push({ parent: stack[stack.length - 1].value, key: key, value: current })
  }
  const leaf = stack.pop()
  if (!leaf || !leaf.parent || !leaf.key) return
  delete leaf.parent[leaf.key]
  while (stack.length > 1) {
    const node = stack.pop()
    if (!node || !node.parent || !node.key) continue
    const value = node.parent[node.key]
    if (!value || typeof value !== 'object' || Array.isArray(value) || Object.keys(value).length) break
    delete node.parent[node.key]
  }
}

const fnPickFirstDefined = function (source, paths) {
  const safeSource = (source && typeof source === 'object') ? source : {}
  const list = Array.isArray(paths) ? paths : []
  for (let i = 0; i < list.length; i += 1) {
    const path = String(list[i] || '').trim()
    if (!path) continue
    const value = fnGetByPath(safeSource, path)
    if (value === undefined) continue
    return value
  }
  return undefined
}

const fnAssignScopedTurnSource = function (target, targetPath, source, sourcePaths) {
  const value = fnPickFirstDefined(source, sourcePaths)
  if (value === undefined) return
  fnSetByPath(target, targetPath, value)
}

const fnBuildScopeTurnSource = function (pointProps, folderProps) {
  const out = {}
  fnAssignScopedTurnSource(out, 'wpml:useGlobalTurnParam', pointProps, [
    'wpml:useGlobalTurnParam',
    'useGlobalTurnParam'
  ])
  fnAssignScopedTurnSource(out, 'wpml:waypointTurnParam', pointProps, [
    'wpml:waypointTurnParam',
    'waypointTurnParam'
  ])
  fnAssignScopedTurnSource(out, 'Folder.wpml:globalWaypointTurnMode', pointProps, [
    'Folder.wpml:globalWaypointTurnMode',
    'Folder.globalWaypointTurnMode',
    'wpml:globalWaypointTurnMode',
    'globalWaypointTurnMode'
  ])
  fnAssignScopedTurnSource(out, 'Folder.wpml:globalWaypointTurnMode', folderProps, [
    'wpml:globalWaypointTurnMode',
    'globalWaypointTurnMode',
    'Folder.wpml:globalWaypointTurnMode',
    'Folder.globalWaypointTurnMode'
  ])
  return out
}

const fnPruneKmzTurnCache = function (props) {
  const safeProps = (props && typeof props === 'object') ? props : null
  if (!safeProps) return
  KMZ_TURN_ROOT_CACHE_PATHS.forEach((path) => {
    fnDeletePath(safeProps, path)
  })
  ;['template', 'waylines'].forEach((scopeId) => {
    KMZ_TURN_SCOPED_CACHE_PATHS.forEach((path) => {
      fnDeletePath(safeProps, scopeId + '.' + path)
    })
  })
}

const buildKmzScopedTurnPropsFromSources = function ({
  templatePointProps,
  waylinePointProps,
  templateFolderProps,
  waylinesFolderProps
} = {}) {
  return {
    template: fnBuildScopeTurnSource(templatePointProps, templateFolderProps),
    waylines: fnBuildScopeTurnSource(waylinePointProps, waylinesFolderProps)
  }
}

const applyKmzScopedTurnProps = function (props, scopedTurnProps) {
  const safeProps = (props && typeof props === 'object') ? props : null
  if (!safeProps) return safeProps
  const scoped = (scopedTurnProps && typeof scopedTurnProps === 'object') ? scopedTurnProps : {}
  fnPruneKmzTurnCache(safeProps)
  ;['template', 'waylines'].forEach((scopeId) => {
    const scopeProps = scoped[scopeId]
    if (!scopeProps || typeof scopeProps !== 'object') return
    fnAssignScopedTurnSource(safeProps, scopeId + '.wpml:useGlobalTurnParam', scopeProps, [
      'wpml:useGlobalTurnParam',
      'useGlobalTurnParam'
    ])
    fnAssignScopedTurnSource(safeProps, scopeId + '.wpml:waypointTurnParam', scopeProps, [
      'wpml:waypointTurnParam',
      'waypointTurnParam'
    ])
    fnAssignScopedTurnSource(safeProps, scopeId + '.Folder.wpml:globalWaypointTurnMode', scopeProps, [
      'Folder.wpml:globalWaypointTurnMode',
      'Folder.globalWaypointTurnMode',
      'wpml:globalWaypointTurnMode',
      'globalWaypointTurnMode'
    ])
  })
  return safeProps
}

const syncKmzPointTurnScopesFromXml = function (point, {
  fnXmlToObj,
  templateFolderProps,
  waylinesFolderProps,
  templateFolderEl,
  waylinesFolderEl
} = {}) {
  if (!point || typeof fnXmlToObj !== 'function') return point
  if (!point.props || typeof point.props !== 'object') point.props = {}
  const templatePointProps = point.xmlEl ? fnXmlToObj(point.xmlEl) : {}
  const waylinePointEl = Array.isArray(point.linkedXmlEls) ? (point.linkedXmlEls[0] || null) : null
  const waylinePointProps = waylinePointEl ? fnXmlToObj(waylinePointEl) : {}
  const scoped = buildKmzScopedTurnPropsFromSources({
    templatePointProps: templatePointProps,
    waylinePointProps: waylinePointProps,
    templateFolderProps: templateFolderProps || (templateFolderEl ? fnXmlToObj(templateFolderEl) : {}),
    waylinesFolderProps: waylinesFolderProps || (waylinesFolderEl ? fnXmlToObj(waylinesFolderEl) : {})
  })
  applyKmzScopedTurnProps(point.props, scoped)
  return point
}

const syncKmzMissionTurnScopesFromXml = function (missionState, fnXmlToObj) {
  if (!missionState || !Array.isArray(missionState.waypoints) || typeof fnXmlToObj !== 'function') return missionState
  const templateFolderProps = missionState.folderEl ? fnXmlToObj(missionState.folderEl) : {}
  const waylinesFolderProps = missionState.secondaryFolderEl ? fnXmlToObj(missionState.secondaryFolderEl) : {}
  missionState.waypoints.forEach((point) => {
    syncKmzPointTurnScopesFromXml(point, {
      fnXmlToObj: fnXmlToObj,
      templateFolderProps: templateFolderProps,
      waylinesFolderProps: waylinesFolderProps
    })
  })
  return missionState
}

const fnNormalizeTurnPathForCheck = function (path) {
  let normalized = String(path || '').trim()
  if (!normalized) return ''
  const lower = normalized.toLowerCase()
  if (lower.indexOf('template.') === 0) normalized = normalized.slice(9)
  if (String(normalized).toLowerCase().indexOf('waylines.') === 0) normalized = normalized.slice(9)
  return String(normalized || '').trim().toLowerCase()
}

const isKmzTurnFieldPath = function (path) {
  const normalized = fnNormalizeTurnPathForCheck(path)
  if (!normalized) return false
  return normalized === 'wpml:useglobalturnparam' ||
    normalized === 'useglobalturnparam' ||
    normalized.indexOf('wpml:waypointturnparam') === 0 ||
    normalized.indexOf('waypointturnparam') === 0 ||
    normalized === 'wpml:globalwaypointturnmode' ||
    normalized === 'globalwaypointturnmode' ||
    normalized === 'folder.wpml:globalwaypointturnmode' ||
    normalized === 'folder.globalwaypointturnmode'
}

const isKmzGlobalTurnFieldPath = function (path) {
  const normalized = fnNormalizeTurnPathForCheck(path)
  if (!normalized) return false
  return normalized === 'wpml:globalwaypointturnmode' ||
    normalized === 'globalwaypointturnmode' ||
    normalized === 'folder.wpml:globalwaypointturnmode' ||
    normalized === 'folder.globalwaypointturnmode'
}

const fnResolveTurnValueByUseCase = function (pointOrProps, useCase, mode, childName, fallbackValue) {
  const point = (pointOrProps && pointOrProps.props && typeof pointOrProps.props === 'object') ? pointOrProps : null
  const props = point ? point.props : ((pointOrProps && typeof pointOrProps === 'object') ? pointOrProps : {})
  const scopePriority = KMZ_TURN_SCOPE_PRIORITY[useCase] || KMZ_TURN_SCOPE_PRIORITY.playback
  const child = String(childName || '').trim()
  for (let i = 0; i < scopePriority.length; i += 1) {
    const scopeId = scopePriority[i]
    let paths = []
    if (mode === 'useGlobal') {
      paths = [
        scopeId + '.wpml:useGlobalTurnParam',
        scopeId + '.useGlobalTurnParam'
      ]
    } else if (mode === 'local' && child) {
      paths = [
        scopeId + '.wpml:waypointTurnParam.wpml:' + child,
        scopeId + '.wpml:waypointTurnParam.' + child,
        scopeId + '.waypointTurnParam.wpml:' + child,
        scopeId + '.waypointTurnParam.' + child
      ]
    } else if (mode === 'global') {
      paths = [
        scopeId + '.Folder.wpml:globalWaypointTurnMode',
        scopeId + '.Folder.globalWaypointTurnMode',
        scopeId + '.wpml:globalWaypointTurnMode',
        scopeId + '.globalWaypointTurnMode'
      ]
    }
    for (let j = 0; j < paths.length; j += 1) {
      const value = fnGetByPath(props, paths[j])
      if (value === undefined || value === null) continue
      if (typeof value === 'string' && !String(value).trim()) continue
      return value
    }
  }
  return fallbackValue
}

const resolveKmzPlaybackUseGlobalTurnParam = function (pointOrProps, fallbackValue) {
  return fnResolveTurnValueByUseCase(pointOrProps, 'playback', 'useGlobal', '', fallbackValue)
}

const resolveKmzPlaybackTurnField = function (pointOrProps, childName, fallbackValue) {
  return fnResolveTurnValueByUseCase(pointOrProps, 'playback', 'local', childName, fallbackValue)
}

const resolveKmzPlaybackGlobalTurnMode = function (pointOrProps, fallbackValue) {
  return fnResolveTurnValueByUseCase(pointOrProps, 'playback', 'global', '', fallbackValue)
}

const resolveKmzTemplateExportUseGlobalTurnParam = function (pointOrProps, fallbackValue) {
  return fnResolveTurnValueByUseCase(pointOrProps, 'templateExport', 'useGlobal', '', fallbackValue)
}

const resolveKmzTemplateExportTurnField = function (pointOrProps, childName, fallbackValue) {
  return fnResolveTurnValueByUseCase(pointOrProps, 'templateExport', 'local', childName, fallbackValue)
}

const resolveKmzTemplateExportGlobalTurnMode = function (pointOrProps, fallbackValue) {
  return fnResolveTurnValueByUseCase(pointOrProps, 'templateExport', 'global', '', fallbackValue)
}

const resolveKmzWaylinesExportUseGlobalTurnParam = function (pointOrProps, fallbackValue) {
  return fnResolveTurnValueByUseCase(pointOrProps, 'waylinesExport', 'useGlobal', '', fallbackValue)
}

const resolveKmzWaylinesExportTurnField = function (pointOrProps, childName, fallbackValue) {
  return fnResolveTurnValueByUseCase(pointOrProps, 'waylinesExport', 'local', childName, fallbackValue)
}

const resolveKmzWaylinesExportGlobalTurnMode = function (pointOrProps, fallbackValue) {
  return fnResolveTurnValueByUseCase(pointOrProps, 'waylinesExport', 'global', '', fallbackValue)
}

const applyKmzResolvedTurnDisplayValuesToMap = function (map, point, useCase) {
  const targetMap = (map && typeof map === 'object') ? map : {}
  const safeUseCase = String(useCase || 'playback').trim()
  const useGlobalValue = fnResolveTurnValueByUseCase(point, safeUseCase, 'useGlobal', '', undefined)
  const globalTurnMode = fnResolveTurnValueByUseCase(point, safeUseCase, 'global', '', undefined)
  if (useGlobalValue !== undefined) {
    targetMap['wpml:useGlobalTurnParam'] = useGlobalValue
  }
  KMZ_TURN_LOCAL_CHILDREN.forEach((childName) => {
    const value = fnResolveTurnValueByUseCase(point, safeUseCase, 'local', childName, undefined)
    if (value !== undefined) {
      targetMap['wpml:waypointTurnParam.wpml:' + childName] = value
    }
  })
  if (globalTurnMode !== undefined) {
    targetMap['wpml:globalWaypointTurnMode'] = globalTurnMode
  }
  return targetMap
}

export {
  buildKmzScopedTurnPropsFromSources,
  applyKmzScopedTurnProps,
  syncKmzPointTurnScopesFromXml,
  syncKmzMissionTurnScopesFromXml,
  isKmzTurnFieldPath,
  isKmzGlobalTurnFieldPath,
  resolveKmzPlaybackUseGlobalTurnParam,
  resolveKmzPlaybackTurnField,
  resolveKmzPlaybackGlobalTurnMode,
  resolveKmzTemplateExportUseGlobalTurnParam,
  resolveKmzTemplateExportTurnField,
  resolveKmzTemplateExportGlobalTurnMode,
  resolveKmzWaylinesExportUseGlobalTurnParam,
  resolveKmzWaylinesExportTurnField,
  resolveKmzWaylinesExportGlobalTurnMode,
  applyKmzResolvedTurnDisplayValuesToMap
}
